import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { soundsAPI } from "../lib/api";
import { getCachedSounds, prefetchSounds } from "../lib/prefetch";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Progress } from "../components/ui/progress";
import {
  Music,
  Play,
  Pause,
  TrendingUp,
  Users,
  Sparkles,
  Filter,
  Loader2,
  Wand2,
} from "lucide-react";
import { toast } from "sonner";
import { BASE_URL } from "../lib/api";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { opacity: 1, scale: 1 },
};

const moodColors = {
  Dramatic: "bg-[#FF0055]/20 text-[#FF0055]",
  Calm: "bg-[#00F0FF]/20 text-[#00F0FF]",
  Energetic: "bg-[#00FF94]/20 text-[#00FF94]",
  Emotional: "bg-[#A855F7]/20 text-[#A855F7]",
  Funny: "bg-[#F59E0B]/20 text-[#F59E0B]",
  Satisfying: "bg-[#EC4899]/20 text-[#EC4899]",
  Relaxed: "bg-[#06B6D4]/20 text-[#06B6D4]",
  Focused: "bg-[#8B5CF6]/20 text-[#8B5CF6]",
};

export default function SoundsPage() {
  const navigate = useNavigate();
  const [sounds, setSounds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [playingId, setPlayingId] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(null);
  const audioRef = useRef(null);

  const categories = [
    "all",
    "Transitions",
    "Lifestyle",
    "Dance",
    "Narrative",
    "Comedy",
    "Before/After",
    "Beauty",
    "How-To",
  ];

  const fetchSounds = useCallback(async () => {
    try {
      // Check for cached data first (only for "all" category for simplicity)
      if (categoryFilter === "all") {
        const cached = getCachedSounds();
        if (cached && cached.length > 0) {
          console.log('[Sounds] Using cached data:', cached.length, 'items');
          setSounds(cached);
          setLoading(false);
          // Refresh in background silently
          prefetchSounds().then(freshData => {
            if (freshData && freshData.length > 0) {
              setSounds(freshData);
            }
          }).catch(() => {});
          return;
        }
      }
      
      // No cache or category filter - fetch from API
      setLoading(true);
      const category = categoryFilter === "all" ? null : categoryFilter;
      const response = await soundsAPI.getTrendingSounds(category);
      setSounds(response.data);
    } catch (error) {
      console.error("Failed to fetch sounds:", error);
    } finally {
      setLoading(false);
    }
  }, [categoryFilter]);

  useEffect(() => {
    fetchSounds();
  }, [fetchSounds]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const handlePreviewSound = async (sound, e) => {
    e.stopPropagation();
    
    // If already playing this sound, stop it
    if (playingId === sound.id) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setPlayingId(null);
      return;
    }
    
    // Stop any currently playing audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    
    setLoadingPreview(sound.id);
    
    try {
      const token = localStorage.getItem("token");
      const previewUrl = `${BASE_URL}/api/sounds/${sound.id}/preview`;
      
      const audio = new Audio();
      audio.crossOrigin = "anonymous";
      
      // Fetch with auth header
      const response = await fetch(previewUrl, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error("Preview not available");
      }
      
      const blob = await response.blob();
      audio.src = URL.createObjectURL(blob);
      
      audio.onended = () => {
        setPlayingId(null);
        audioRef.current = null;
      };
      
      audio.onerror = () => {
        toast.error("Could not play preview");
        setPlayingId(null);
        audioRef.current = null;
      };
      
      audioRef.current = audio;
      await audio.play();
      setPlayingId(sound.id);
    } catch (error) {
      console.error("Preview error:", error);
      toast.error("Preview not available for this sound");
    } finally {
      setLoadingPreview(null);
    }
  };

  const handleUseSound = (sound) => {
    // Stop any playing audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      setPlayingId(null);
    }
    
    // Navigate to create video page with sound data
    navigate("/create", { 
      state: { 
        preselectedSound: {
          id: sound.id,
          name: sound.name,
          mood: sound.mood
        }
      }
    });
    
    toast.success(`"${sound.name}" selected! Create your video now.`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#00F0FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-8"
      data-testid="sounds-page"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight flex items-center gap-3">
            <Music className="w-8 h-8 text-[#00F0FF]" />
            Trending Sounds
          </h1>
          <p className="text-muted-foreground mt-2">
            Discover viral sounds to boost your content&apos;s reach.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-40 bg-secondary/50 border-border/50" data-testid="sound-category-filter">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              {categories.map((cat) => (
                <SelectItem key={cat} value={cat}>
                  {cat === "all" ? "All Categories" : cat}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Info Banner */}
      <Card className="bg-gradient-to-r from-[#00F0FF]/10 to-[#FF0055]/10 border-border/50">
        <CardContent className="p-6 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-[#00F0FF]/20 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-6 h-6 text-[#00F0FF]" />
          </div>
          <div>
            <h3 className="font-semibold text-white">Pro Tip</h3>
            <p className="text-sm text-muted-foreground">
              Using trending sounds can increase your video&apos;s reach by up to 40%. 
              Preview sounds and click &quot;Use Sound&quot; to create a video with it!
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Sounds Grid */}
      <motion.div
        variants={containerVariants}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
      >
        {sounds.map((sound, index) => (
          <motion.div key={sound.id} variants={itemVariants}>
            <Card className="bg-card border-border/50 card-hover h-full">
              <CardContent className="p-5">
                {/* Header */}
                <div className="flex items-start justify-between gap-2 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#FF0055] to-[#00F0FF] flex items-center justify-center flex-shrink-0">
                    <Music className="w-6 h-6 text-white" />
                  </div>
                  <Badge className={moodColors[sound.mood] || "bg-muted"}>
                    {sound.mood}
                  </Badge>
                </div>

                {/* Info */}
                <h3 className="font-semibold text-white text-sm leading-tight mb-1 line-clamp-2">
                  {sound.name}
                </h3>
                <p className="text-xs text-muted-foreground mb-3">{sound.artist}</p>

                {/* Category */}
                <Badge variant="outline" className="text-xs mb-4">
                  {sound.category}
                </Badge>

                {/* Trending Score */}
                <div className="mb-4">
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-muted-foreground flex items-center gap-1">
                      <TrendingUp className="w-3 h-3" />
                      Trending Score
                    </span>
                    <span className="text-white font-medium">{sound.trending_score}%</span>
                  </div>
                  <Progress value={sound.trending_score} className="h-1.5" />
                </div>

                {/* Usage */}
                <div className="flex items-center gap-1 text-xs text-muted-foreground mb-4">
                  <Users className="w-3 h-3" />
                  <span>{sound.usage_count} videos</span>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2">
                  {/* Preview Button */}
                  <Button
                    onClick={(e) => handlePreviewSound(sound, e)}
                    variant="outline"
                    className={`flex-1 rounded-lg gap-2 ${
                      playingId === sound.id 
                        ? 'bg-[#FF0055]/20 border-[#FF0055] text-[#FF0055]' 
                        : 'border-border/50 hover:border-[#00F0FF]/50'
                    }`}
                    data-testid={`preview-sound-btn-${sound.id}`}
                  >
                    {loadingPreview === sound.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : playingId === sound.id ? (
                      <Pause className="w-4 h-4" />
                    ) : (
                      <Play className="w-4 h-4" />
                    )}
                    {playingId === sound.id ? "Stop" : "Preview"}
                  </Button>
                  
                  {/* Use Sound Button */}
                  <Button
                    onClick={() => handleUseSound(sound)}
                    className="flex-1 bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-[#0A0A0B] font-semibold rounded-lg gap-2"
                    data-testid={`use-sound-btn-${sound.id}`}
                  >
                    <Wand2 className="w-4 h-4" />
                    Use Sound
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </motion.div>
    </motion.div>
  );
}
